import React, { useCallback } from "react";
import { AttachmentWithStatus } from "@/entities/attachments/types";
import Text from "@/components/ui/text";
import { Close, Download, File, Loader } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";
import { useGetAttachment } from "@/entities/attachments/hooks";
import {
  contentTypeMap,
  downloadBlob,
  getErrorByCode
} from "@/entities/attachments/helpers";
import { IconButton } from "@/components/ui/icon-button";

// Blob URLs run as the app's origin, so only script-free types are previewed.
const INLINE_PREVIEW_CONTENT_TYPES = new Set([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/gif",
  "text/plain",
  "text/csv"
]);

interface Props {
  attachment: AttachmentWithStatus;
  onRemove?: (attachmentId: string) => void;
  size?: "small" | "large";
  isReadOnly?: boolean;
  isFullSize?: boolean;
  hideRemove?: boolean;
  hideDownload?: boolean;
  isGrouped?: boolean;
  removeDisabled?: boolean;
  removeDisabledTooltipText?: string;
  // Selector stem for this item. Callers rendering a list pass an
  // attachment-scoped value so each row addresses uniquely.
  dataTest?: string;
}

const FileItem: React.FC<Props> = ({
  attachment,
  onRemove,
  size = "small",
  isReadOnly = false,
  isFullSize = false,
  hideRemove = false,
  hideDownload = false,
  isGrouped = false,
  removeDisabled = false,
  removeDisabledTooltipText,
  dataTest
}) => {
  const { t } = useTranslation();
  const { id, filename, isUploading, error } = attachment;
  const { refetch: getAttachment } = useGetAttachment(
    isUploading || Boolean(error) ? "" : id,
    { eager: !isReadOnly }
  );

  const onDownload = useCallback(async () => {
    try {
      const { data } = await getAttachment();
      if (data) {
        downloadBlob(data);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  }, [getAttachment]);

  const openFile = useCallback(async () => {
    if (isUploading || Boolean(error)) return;

    try {
      const { data } = await getAttachment();
      if (data) {
        const extension = filename.split(".").pop()?.toLowerCase() || "";
        const contentType =
          contentTypeMap[extension] || "application/octet-stream";
        const blob = new Blob([data.blob], { type: contentType });
        const blobUrl = URL.createObjectURL(blob);
        if (INLINE_PREVIEW_CONTENT_TYPES.has(contentType)) {
          window.open(blobUrl, "_blank", "noopener,noreferrer");
        } else {
          downloadBlob({ blob, filename: data.filename });
        }
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      }
    } catch (err) {
      console.error("Error opening file:", err);
    }
  }, [id, isUploading, error, getAttachment, filename]);

  // Small and Large carry different icon scales in the design system: 16px
  // glyphs and actions on Small, 20px on Large.
  const glyphSize = size === "small" ? "sm" : "5";
  const actionButtonSize = size === "small" ? "xs" : "md";

  const renderXButton = () => (
    <IconButton
      variant="iconNoBg"
      aria-label={t("remove")}
      onClick={() => onRemove?.(id)}
      size={actionButtonSize}
      iconColor="secondary"
      iconClassName="[&>svg]:size-full"
      disabled={removeDisabled}
      tooltipText={removeDisabled ? removeDisabledTooltipText : undefined}
    >
      <Close />
    </IconButton>
  );
  return (
    <div
      className={cn(
        "flex flex-col gap-1",
        isFullSize || error ? "w-full" : "w-fit"
      )}
    >
      <div
        className={cn(
          "group relative flex items-center border-[0.5px]",
          // Grouped rows sit flush inside a clipping container that owns the
          // outer radius, so only a standalone row rounds itself.
          !isGrouped && "rounded-lg",
          size === "large"
            ? "p-2"
            : isReadOnly
              ? "py-1 px-[6px]"
              : "py-1 pl-[6px] pr-1",
          !isFullSize &&
            (size === "small" ? "min-w-[80px] max-w-[240px]" : "max-w-[720px]"),
          isFullSize
            ? "w-full justify-between"
            : size === "small"
              ? "space-x-[6px]"
              : "space-x-6",
          error
            ? "border-border-badge-negative-strong bg-surface-destructive-hover"
            : "border-border-badge-chip-default bg-white-6",
          // Hover layers a second translucent fill over the base rather than
          // swapping it, matching how the design composites the two.
          !error &&
            !isReadOnly &&
            "after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:bg-white-4 after:opacity-0 hover:after:opacity-100"
        )}
        data-test={dataTest}
      >
        <button
          type="button"
          // A real button rather than a clickable div: opening the file is the
          // primary action on a saved attachment, and it has to be reachable
          // without a pointer.
          disabled={isUploading || Boolean(error)}
          aria-label={t("filesUpload.openFile", { filename })}
          data-test={dataTest ? `${dataTest}-open` : undefined}
          className={cn(
            "relative flex items-center min-w-0 text-left bg-transparent p-0",
            size === "small" ? "gap-1" : "gap-2",
            isFullSize ? "flex-1" : "max-w-[12rem]",
            !isUploading && "cursor-pointer"
          )}
          onClick={openFile}
        >
          {isUploading ? (
            <IconContainer size={glyphSize}>
              <Loader />
            </IconContainer>
          ) : (
            <IconContainer
              size={glyphSize}
              color="primary"
              className={cn(
                !error &&
                  "group-hover:!text-link transition-colors ease-in-out duration-200"
              )}
            >
              <File />
            </IconContainer>
          )}
          <Text
            variant="body"
            hasTruncatedTooltip
            className={cn(
              "truncate",
              // A failed row cannot be opened, so it must not offer the link
              // affordance the hover rule would otherwise imply.
              // Read-only chips read the same as an editable row's filename:
              // default foreground at rest, link color on hover. The design
              // keeps blue for the hover state alone — a permanently blue chip
              // reads as a pressed or visited link on a surface where nothing
              // has been interacted with yet.
              !isUploading &&
                !error &&
                "group-hover:!text-link transition-colors ease-in-out duration-200"
            )}
          >
            {filename}
          </Text>
        </button>
        {!isReadOnly && (
          <div
            className={cn(
              "relative flex items-center shrink-0",
              size === "large" && "gap-1"
            )}
          >
            {isUploading && !error && !hideDownload ? (
              // The download slot holds its footprint while the upload runs, so
              // the chip does not reflow the moment it completes.
              <div
                className={cn("shrink-0", size === "small" ? "size-5" : "size-6")}
                aria-hidden
              />
            ) : null}
            {!isUploading && !error && !hideDownload && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconButton
                    variant="iconNoBg"
                    aria-label={t("download")}
                    onClick={onDownload}
                    size={actionButtonSize}
                    iconColor="secondary"
                    iconClassName="[&>svg]:size-full"
                  >
                    <Download />
                  </IconButton>
                </TooltipTrigger>
                <TooltipContent>{t("download")}</TooltipContent>
              </Tooltip>
            )}
            {/* A caller that supplies no handler has no remove semantics, so
                the control must not be offered — rendering it unconditionally
                leaves a button that looks live and does nothing. */}
            {!hideRemove && onRemove && (
              <>
                {size === "large" && !removeDisabled ? (
                  <Tooltip>
                    <TooltipTrigger asChild>{renderXButton()}</TooltipTrigger>
                    <TooltipContent>{t("remove")}</TooltipContent>
                  </Tooltip>
                ) : (
                  renderXButton()
                )}
              </>
            )}
          </div>
        )}
      </div>
      {error ? (
        <Text className="pl-1" variant="smBodyError">
          {getErrorByCode(t, error)}
        </Text>
      ) : null}
    </div>
  );
};

export default FileItem;
